import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { AdminFeedbackService } from '../../../core/feedback/admin-feedback.service';
import {
  AdminFeedbackReport,
  FeedbackFilterOption,
  FeedbackSummary,
} from '../../../core/feedback/feedback.types';
import { IconComponent } from '../../../shared/icon/icon';
import { StatusChipComponent } from '../../../shared/status-chip/status-chip';
import { DashPipe } from '../../../shared/pipes/dash.pipe';

/**
 * Admin triage worklist for suggestions and bug reports submitted from the
 * in-app Report button.
 *
 * Defaults to the open list — everything not yet resolved, won't-fixed or
 * marked duplicate — because the question this page exists to answer is "what
 * still needs doing". The toggle widens it to everything ever reported.
 */
@Component({
  selector: 'app-admin-feedback',
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    IconComponent,
    StatusChipComponent,
    DashPipe,
  ],
  templateUrl: './admin-feedback.html',
  styleUrl: './admin-feedback.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminFeedbackComponent implements OnInit {
  private readonly service = inject(AdminFeedbackService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly reports = signal<AdminFeedbackReport[]>([]);
  protected readonly summary = signal<FeedbackSummary | null>(null);
  protected readonly statusOptions = signal<FeedbackFilterOption[]>([]);
  protected readonly kindOptions = signal<FeedbackFilterOption[]>([]);

  protected readonly loading = signal(true);
  protected readonly error = signal(false);

  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly total = signal(0);
  protected readonly hasNext = signal(false);

  protected readonly statusFilter = signal('');
  protected readonly kindFilter = signal('');
  protected readonly search = signal('');
  protected readonly openOnly = signal(true);

  /** True when a filter is narrowing the list — drives the no-results state. */
  protected readonly isFiltered = computed(
    () => !!this.statusFilter() || !!this.kindFilter() || !!this.search(),
  );

  protected readonly rangeStart = computed(() =>
    this.total() === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1,
  );
  protected readonly rangeEnd = computed(() =>
    Math.min(this.page() * this.pageSize(), this.total()),
  );

  /**
   * Whether the results span more than one page.
   *
   * Style guide: *"Hide the paginator entirely when results fit one page."*
   * A pager over a single row is two dead buttons and a count the table already
   * shows. ⚠️ Not `total() > 0` — that is true of one row.
   *
   * Read off `hasNext` rather than `ceil(total / pageSize)` so it stays correct
   * for an endpoint that reports `has_next` without a reliable total, and the
   * `page() > 1` limb keeps the pager present on the last page, which is the
   * only way back.
   */
  protected readonly showPager = computed(() => this.hasNext() || this.page() > 1);

  ngOnInit(): void {
    this.load();
  }

  protected retry(): void {
    this.load();
  }

  protected onStatusChange(value: string): void {
    this.statusFilter.set(value);
    this.resetToFirstPage();
  }

  protected onKindChange(value: string): void {
    this.kindFilter.set(value);
    this.resetToFirstPage();
  }

  protected onSearch(value: string): void {
    this.search.set(value.trim());
    this.resetToFirstPage();
  }

  protected onOpenOnlyChange(value: boolean): void {
    this.openOnly.set(value);
    this.resetToFirstPage();
  }

  protected clearFilters(): void {
    this.statusFilter.set('');
    this.kindFilter.set('');
    this.search.set('');
    this.resetToFirstPage();
  }

  protected prevPage(): void {
    if (this.page() > 1) {
      this.page.update(p => p - 1);
      this.load();
    }
  }

  protected nextPage(): void {
    if (this.hasNext()) {
      this.page.update(p => p + 1);
      this.load();
    }
  }

  private resetToFirstPage(): void {
    this.page.set(1);
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(false);

    this.service
      .list({
        page: this.page(),
        page_size: this.pageSize(),
        triage_status: this.statusFilter() || undefined,
        kind: this.kindFilter() || undefined,
        search: this.search() || undefined,
        open_only: this.openOnly(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.reports.set(data.results);
          this.summary.set(data.summary);
          this.statusOptions.set(data.filters.triage_statuses);
          this.kindOptions.set(data.filters.kinds);
          this.total.set(data.total);
          this.hasNext.set(data.has_next);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }
}
